import Swup from 'swup';
import SwupHeadPlugin from '@swup/head-plugin';
import SwupPreloadPlugin from '@swup/preload-plugin';
import SwupScrollPlugin from '@swup/scroll-plugin';
import SwupA11yPlugin from '@swup/a11y-plugin';
import SwupJsPlugin from '@swup/js-plugin';
import { initHistory, stampHistoryEntry, trackDirection } from './history.js';
import { resolveTransition } from './transitions.js';
import { initHashScroll } from './hash-scroll.js';
import { initQueryCascade } from '../transitions/query-cascade.js';
import { initCardMorph } from '../transitions/card-morph.js';

/**
 * Canonical window event dispatched after every Swup content swap settles.
 *
 * Persistent chrome — anything rendered OUTSIDE `#swup` (header, footer,
 * modals, announcement bars) — is never destroyed/re-initialised by Alpine's
 * per-navigation teardown, which only runs on `#swup`. Such components hold
 * references to nodes that Swup replaces and silently freeze after the first
 * navigation. They MUST listen for this event to re-acquire their DOM refs and
 * recompute state, and MUST NOT reach into Swup's hooks directly (the router
 * instance is module-private; see `navigateTo`).
 *
 * @type {string}
 */
export const PAGE_VIEW_EVENT = 'psd:page-view';

/**
 * Module-scoped handle on the running router, captured in {@link createRouter}.
 *
 * The ONLY reference to the live Swup instance outside this module boundary —
 * every other module navigates through {@link navigateTo}, never the raw
 * instance, per the "wrap third-party APIs" invariant. `window.swup` does not
 * exist in this kit; components that assume it would silently no-op.
 *
 * @type {import('swup').default | null}
 */
let router = null;

/**
 * Body-level `data-*` keys the router owns and must never copy from the
 * swapped-in document. `data-transition` is resolved per visit in the
 * `visit:start` hook; overwriting it with the incoming document's server default
 * would clobber the resolved transition mid-navigation. Keyed the dataset way
 * (camelCase, no `data-` prefix).
 *
 * @type {ReadonlySet<string>}
 */
const ROUTER_OWNED_BODY_DATA = new Set(['transition']);

/**
 * Dispatch the canonical {@link PAGE_VIEW_EVENT} on `window`.
 *
 * @param {{ url?: string, title?: string, visit?: (import('swup').Visit | null) }} detail
 *   — `url` and `title` of the page now shown; `visit` is the Swup visit that
 *   produced it, or `null` on the initial server-rendered load.
 * @returns {void}
 */
function dispatchPageView(detail) {
    window.dispatchEvent(new CustomEvent(PAGE_VIEW_EVENT, { detail }));
}

/**
 * Copy `data-*` attributes from the swapped-in document's `<body>` onto the live
 * `<body>` so template-declared page state (namespace, theme, section, template
 * handle …) stays truthful across SPA navigations.
 *
 * The `<body>` lives OUTSIDE `#swup`, so without this its server-rendered
 * attributes freeze on the first page and the transition registry, shader bridge,
 * and analytics content_group all resolve against the wrong page forever. Every
 * data attribute is synced except {@link ROUTER_OWNED_BODY_DATA}; attributes
 * absent from the incoming body are removed so stale page state does not linger.
 *
 * @param {(HTMLElement | null | undefined)} incomingBody — `visit.to.document.body`
 * @returns {void}
 */
function syncBodyDataset(incomingBody) {
    if (!incomingBody) {
        return;
    }

    const currentBody = document.body;

    // Add/update every incoming data attribute except router-owned ones.
    for (const key in incomingBody.dataset) {
        if (!ROUTER_OWNED_BODY_DATA.has(key)) {
            currentBody.dataset[key] = incomingBody.dataset[key];
        }
    }

    // Drop stale data attributes no longer present on the incoming body. Snapshot
    // the keys first — deleting from the live DOMStringMap mid-iteration would
    // skip entries.
    for (const key of Object.keys(currentBody.dataset)) {
        if (!ROUTER_OWNED_BODY_DATA.has(key) && !(key in incomingBody.dataset)) {
            delete currentBody.dataset[key];
        }
    }
}

/**
 * Create and configure the Swup router instance.
 *
 * Registers all plugins, wires history direction tracking, transition
 * resolution, and data-transition attribute updates. Also wires the
 * persistent-chrome contract: syncs body-level data attributes from the
 * swapped-in document and dispatches {@link PAGE_VIEW_EVENT} once each
 * navigation settles and once on the initial load.
 *
 * @returns {Swup} Configured Swup instance
 */
export function createRouter() {
    // Stamp initial history entry with psdIndex
    initHistory();

    const swup = new Swup({
        containers: ['#swup'],
        native: true,
        cache: true,
        // BACK AND FORWARD MUST ANIMATE, and Swup's default is that they do not.
        // `handlePopState` sets `visit.animation.animate = false` on every history
        // visit unless this is on, and `performNavigation` then takes the
        // `animation:skip` branch — which never calls `document.startViewTransition`.
        // So without this the whole reverse path is dead: the `--reverse` suffix
        // resolved in `before('content:replace')` is written to `data-transition`
        // and drives nothing, and the reversible card morph names the hero on the
        // outgoing side of a Back and is never snapshotted. The listing → detail →
        // Back journey is the most common one on this site, so half the morph was
        // unreachable. @swup/scroll-plugin is written for this pairing: it takes
        // `history.scrollRestoration` to `manual` when this flag is on and restores
        // the reader's cached position itself, so Back still returns them to where
        // they were rather than to the top.
        animateHistoryBrowsing: true,
        // Pure hash links (`href="#..."`) never enter Swup — routed through it they
        // would land inside a native view transition that freezes the document around
        // the scroll, bypassing the CSS smooth scrolling that should animate them.
        // Left to the browser, a same-page `#anchor` click scrolls live, honours each
        // target's scroll-margin-top (blocks/_controls.css), and re-targets through
        // layout shifts. Deep links loaded WITH a hash are handled by initHashScroll
        // below. `[data-no-swup]` is Swup's own default ignore rule, preserved here.
        ignoreVisit: (url, { el } = {}) =>
            el?.getAttribute('href')?.startsWith('#') || el?.closest('[data-no-swup]') != null,
        plugins: [
            new SwupHeadPlugin(),
            new SwupPreloadPlugin(),
            new SwupScrollPlugin(),
            new SwupA11yPlugin(),
            new SwupJsPlugin([
                {
                    from: '(.*)',
                    to: '(.*)',
                    out: (done) => done(),
                    in: (done) => done(),
                },
            ]),
        ],
    });

    // Track direction on visit start — history bookkeeping must run before the
    // swap. This sets visit.direction (used below for the reverse suffix).
    swup.hooks.on('visit:start', (visit) => {
        trackDirection(visit);
    });

    // Resolve the route-pair transition once the incoming page is parsed.
    // resolveTransition reads the TO namespace from visit.to.document.body, which
    // Swup v4 only populates after the fetch resolves — so this MUST run post-fetch,
    // not on visit:start (where TO was always undefined → every visit crossfaded).
    // before('content:replace') is the first hook where to.document is guaranteed
    // present and still ahead of the DOM swap and the incoming VT snapshot.
    swup.hooks.before('content:replace', (visit) => {
        // Sets visit.animation.name from the FROM/TO body[data-namespace] pair.
        resolveTransition(visit);

        // Re-apply the reverse suffix for backward navigations (trackDirection
        // sets visit.direction on visit:start, before the name is resolved).
        if (
            visit.direction === 'backward' &&
            visit.animation.name &&
            !visit.animation.name.endsWith('--reverse')
        ) {
            visit.animation.name += '--reverse';
        }

        // Update body data-transition attribute for CSS view-transition scoping.
        document.body.dataset.transition = visit.animation.name;
    });

    // Stamp psdIndex onto the entry Swup has now pushed. This CANNOT run on
    // visit:start — the entry does not exist yet, which is why the old
    // `visit.to.state.psdIndex = …` assignment silently did nothing and left
    // every backward visit misread as forward.
    swup.hooks.on('visit:end', (visit) => {
        stampHistoryEntry(visit);
    });

    // Close any open navigation on a visit. The header and its MENU panel render
    // OUTSIDE `#swup`, so Alpine never destroys them and an open panel would
    // otherwise survive the swap and sit over the page the reader just chose.
    swup.hooks.on('visit:start', () => {
        const nav = window.Alpine?.store('nav');

        if (nav) {
            nav.mobileOpen = false;
            nav.menuOpen = false;
        }
    });

    // Sync body-level data attributes from the swapped-in document (see
    // syncBodyDataset). The incoming document is parsed and available by
    // content:replace time.
    swup.hooks.on('content:replace', (visit) => {
        syncBodyDataset(visit?.to?.document?.body);
    });

    // Dispatch the canonical persistent-chrome signal once the new DOM, head,
    // and scroll position have settled (page:view, not content:replace). Chrome
    // outside #swup listens for this to re-acquire refs and recompute — see
    // PAGE_VIEW_EVENT. Swup passes { url, title } as the hook's second argument;
    // the visit is forwarded so listeners can inspect direction, trigger, etc.
    swup.hooks.on('page:view', (visit, args) => {
        dispatchPageView({
            url: args?.url ?? visit?.to?.url,
            title: args?.title ?? document.title,
            visit,
        });
    });

    // Query-only visits (filter bars / pill removal): suppress the whole-viewport
    // view transition + scroll reset and cascade only the result cards. Wired with
    // kit-default selectors ([data-results-grid] / [data-results-card]); projects
    // point these at their listing markup. See transitions/query-cascade.js.
    initQueryCascade(swup);

    // Reversible card ↔ detail View-Transition morph. Wired with kit-default
    // markers ([data-card-morph] / [data-card-morph-hero] / data-card-id); a
    // graceful no-op without document.startViewTransition. See transitions/card-morph.js.
    initCardMorph(swup);

    // Deep-link fragment scroll — re-apply the URL's #hash on Swup's page:view and,
    // for the initial load, a user-scroll-aware retry across the boot-settle window.
    // Pure same-page # clicks are left to the browser (see ignoreVisit above).
    initHashScroll(swup);

    router = swup;

    // Swup's `page:view` hook fires only after a content swap, never on the
    // initial server-rendered load. Mint the first event ourselves so persistent
    // chrome runs the same re-acquire codepath on load as on every later visit.
    // Deferred to a microtask so listeners wired synchronously during app boot
    // (after createRouter returns) are attached before it fires; `visit` is null
    // because no Swup visit produced the initial page.
    queueMicrotask(() => {
        dispatchPageView({
            url: window.location.pathname + window.location.search,
            title: document.title,
            visit: null,
        });
    });

    return swup;
}

/**
 * Navigate to a URL via Swup, without a full page reload — the wrapped
 * programmatic-navigation API for modules that don't hold the router instance
 * (e.g. a form submit handler routing to a thank-you page, a custom control).
 *
 * This is the ONLY sanctioned way for component code to trigger navigation:
 * template and component code must never touch the Swup instance directly.
 * Falls back to a full browser navigation when called before the router has
 * booted, so callers are never left stuck (server-first invariant).
 *
 * @param {string} url — Destination URL or path
 * @param {object} [options] — Swup navigation options (forwarded to swup.navigate)
 * @returns {void}
 */
export function navigateTo(url, options = {}) {
    if (!url) {
        return;
    }

    if (router) {
        router.navigate(url, options);
    } else {
        window.location.assign(url);
    }
}
